import React, { useEffect, useRef } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';

// refer to https://www.npmjs.com/package/react-confirm for documentation

interface Props {
  confirmation: React.ReactElement;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const Confirmation: ConfirmDialog<Props, boolean> = ({
  confirmation,
  confirmButtonText,
  cancelButtonText,
  proceed,
  show,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);
  return (
    <Dialog ref={dialogRef}>
      {confirmation}
      <Buttons>
        <Button onClick={() => proceed(true)}>{confirmButtonText ?? 'Yes'}</Button>
        <Button onClick={() => proceed(false)}>{cancelButtonText ?? 'No'}</Button>
      </Buttons>
    </Dialog>
  );
};

const Alert: ConfirmDialog<Props, boolean> = ({ confirmation, proceed, show }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);
  return (
    <Dialog ref={dialogRef}>
      {confirmation}
      <Buttons>
        <Button onClick={() => proceed(false)}>Ok</Button>
      </Buttons>
    </Dialog>
  );
};

interface CustomProps extends Props {
  promptTrue: React.ReactElement;
  promptFalse: React.ReactElement;
}

const CustomConfirmation: ConfirmDialog<CustomProps, boolean> = ({
  confirmation,
  promptTrue,
  promptFalse,
  proceed,
  show,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);
  return (
    <Dialog ref={dialogRef}>
      {confirmation}
      <StackedButtons>
        <Button onClick={() => proceed(true)}>{promptTrue}</Button>
        <Button onClick={() => proceed(false)}>{promptFalse}</Button>
      </StackedButtons>
    </Dialog>
  );
};

const Dialog = styled.dialog`
  border: var(--color-confirmation-border) solid 5px;
  border-radius: 20px;
  position: fixed;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
  margin: auto;
  background-color: var(--color-confirmation-background);
  padding: 20px;
  top: 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 30px;
`;

const StackedButtons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 20px;
  gap: 10px;
`;

const Button = styled.button`
  font-size: 1em;
  border: none;
  color: white;
  background-color: black;
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
`;

export const confirm = createConfirmation(confirmable(Confirmation));

export const alert = createConfirmation(confirmable(Alert));

export const customConfirm = createConfirmation(confirmable(CustomConfirmation));
